import { useCallback, useEffect, useRef, useState } from 'react'

export const useTimeout = (callback: () => void, initialDelay: number | null) => {
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedCallback = useRef<() => void>()
  const [delay, setDelay] = useState<number | null>(initialDelay)

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (delay === null) return

    const tick = () => {
      if (savedCallback.current) {
        savedCallback.current()
      }
    }

    timeoutId.current = setTimeout(tick, delay)

    return () => clearTimeout(timeoutId.current as ReturnType<typeof setTimeout>)
  }, [delay])

  const clear = useCallback(() => {
    if (timeoutId.current !== null) {
      clearTimeout(timeoutId.current)
      setDelay(null)
      timeoutId.current = null
    }
  }, [])

  const setTimeoutDelay = useCallback(
    (newDelay: number | null) => {
      clear()
      setDelay(newDelay)
    },
    [clear],
  )

  return { clear, setTimeoutDelay }
}
